import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import {
  CheckCircleIcon,
  CircleIcon,
  FilterLinesIcon,
  Trash01Icon,
} from '@langchain/untitled-ui-icons';
import { CircularProgress } from '@mui/joy';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/Dropdown';
import { RunFilterCount } from '@/components/FilterBar/FilterCount';
import { RuleActionLabel } from '@/components/Rules/RuleActionLabel';
import { TableCellTextOverflow } from '@/components/Table';
import { Tooltip } from '@/design-system';
import { Badge } from '@/design-system/components/Badge';
import { Button } from '@/design-system/components/Button';
import { RuleSchema } from '@/types/schema';

const columnHelper = createColumnHelper<RuleSchema>();

export type RulesColumnsDefProps = {
  organizationId: string | null;
  onOpenRuleLogs?: (rule: RuleSchema) => void;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getRulesColumnsDef: (
  props: RulesColumnsDefProps
) => ColumnDef<RuleSchema, any>[] = ({ onOpenRuleLogs }) => [
  columnHelper.accessor('is_enabled', {
    id: 'status',
    header: () => <div className="flex justify-center">Enabled</div>,
    enableResizing: false,
    enableSorting: false,
    size: 40,
    cell: ({ getValue }) => {
      const isEnabled = getValue();
      return isEnabled ? (
        <div className="flex justify-center">
          <CheckCircleIcon className="h-4 w-4 text-success" />
        </div>
      ) : (
        <Tooltip description="This automation is disabled and will not run. Edit the automation to enable it.">
          <div className="flex justify-center">
            <CircleIcon className="h-4 w-4" />
          </div>
        </Tooltip>
      );
    },
  }),
  columnHelper.accessor('display_name', {
    header: () => <div className="pl-3">Rule Name</div>,
    enableResizing: true,
    enableSorting: false,
    cell: ({ getValue }) => {
      const value = getValue();
      return value ? (
        <div className="pl-3">{value}</div>
      ) : (
        <i className="pl-3 text-tertiary">Unnamed rule</i>
      );
    },
  }),
  columnHelper.accessor('sampling_rate', {
    header: 'Sampling Rate',
    enableResizing: true,
    enableSorting: false,
    cell: ({ getValue }) => {
      const value = getValue();
      return (
        <div className="flex items-center gap-2">
          <CircularProgress
            size="sm"
            value={value * 100}
            determinate
            sx={{
              '--CircularProgress-size': '16px',
              '--CircularProgress-progressThickness': '2px',
              '--CircularProgress-trackThickness': '2px',
              transform: 'scaleX(-1)',
            }}
          />
          <span>{(value * 100).toFixed(0)}%</span>
        </div>
      );
    },
    size: 120,
  }),
  columnHelper.accessor('filter', {
    header: 'Filters',
    enableResizing: true,
    enableSorting: false,
    cell: ({ getValue }) => {
      const value = getValue();
      return (
        <Badge
          leftDecorator={FilterLinesIcon}
          rounded="sm"
          color="secondary"
          size="sm"
        >
          <RunFilterCount filter={value} />
        </Badge>
      );
    },
    size: 100,
  }),
  columnHelper.display({
    id: 'action',
    header: 'Action',
    enableResizing: true,
    enableSorting: false,
    cell: ({ row }) => {
      return <RuleActionLabel rule={row.original} />;
    },
    size: 200,
  }),
  columnHelper.display({
    id: 'logs',
    header: 'Logs',
    enableResizing: true,
    enableSorting: false,
    cell: ({ row }) => {
      return (
        <Button
          variant="outlined"
          color="secondary"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onOpenRuleLogs?.(row.original);
          }}
        >
          Logs
        </Button>
      );
    },
    size: 120,
  }),
  getRuleActionColumn(),
];

const getRuleActionColumn = (): ColumnDef<RuleSchema, any> => {
  return columnHelper.display({
    id: 'menu_actions',
    cell: ({ row, table }) => {
      const { actions } = (table.options.meta ?? {}) as {
        actions: {
          handleDelete: (id: string) => void;
        };
      };

      return (
        <TableCellTextOverflow className="justify-end">
          <DropdownMenu>
            <DropdownMenuTrigger
              onClick={(e) => e.stopPropagation()}
              className="flex h-6 w-6 items-center justify-center rounded-md transition-colors hover:bg-secondary focus:outline-none"
            >
              <EllipsisVerticalIcon className="h-5 w-5" />
            </DropdownMenuTrigger>

            <DropdownMenuContent
              className="z-10"
              onClick={(e) => e.stopPropagation()}
            >
              <DropdownMenuItem
                onClick={() => actions.handleDelete(row.original.id)}
                className="flex items-center gap-2"
              >
                <Trash01Icon className="h-5 w-5" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </TableCellTextOverflow>
      );
    },
    enableSorting: false,
    enableHiding: false,
    enableResizing: false,
    size: 40,
  });
};
